<template>
  <div class="verify-page" :class="{ 'is-ready': !!selectedFile, 'is-busy': isVerifying }">
    <div class="bg" aria-hidden="true">
      <span class="bg__blob bg__blob--a" />
      <span class="bg__blob bg__blob--b" />
    </div>

    <header class="topbar">
      <div class="brand">
        <span class="brand__icon" aria-hidden="true">
          <svg viewBox="0 0 32 32" fill="none">
            <rect width="32" height="32" rx="9" fill="url(#brandIcon)" />
            <path d="M16 8.5l7 2.4v5.4c0 4-2.8 7.7-7 8.7-4.2-1-7-4.7-7-8.7v-5.4l7-2.4z" stroke="#fff" stroke-width="1.6" stroke-linejoin="round" />
            <path d="M13 16l2 2 4-4" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            <defs>
              <linearGradient id="brandIcon" x1="4" y1="4" x2="28" y2="28" gradientUnits="userSpaceOnUse">
                <stop stop-color="#0f569a" />
                <stop offset="1" stop-color="#da4c77" />
              </linearGradient>
            </defs>
          </svg>
        </span>
        <span class="brand__name">Verify Letter</span>
      </div>

      <nav class="steps" aria-label="Verification steps">
        <div class="steps__item" :class="{ active: step >= 1, done: step > 1 }">
          <span class="steps__n">
            <svg v-if="step > 1" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
            <template v-else>1</template>
          </span>
          <span class="steps__t">Upload</span>
        </div>
        <span class="steps__rule" :class="{ on: step > 1 }" />
        <div class="steps__item" :class="{ active: step >= 2, done: step > 2 }">
          <span class="steps__n">
            <svg v-if="step > 2" viewBox="0 0 12 12" fill="none" aria-hidden="true"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" /></svg>
            <template v-else>2</template>
          </span>
          <span class="steps__t">Verify</span>
        </div>
        <span class="steps__rule" :class="{ on: step > 2 }" />
        <div class="steps__item" :class="{ active: step >= 3, done: step >= 3 }">
          <span class="steps__n">3</span>
          <span class="steps__t">Result</span>
        </div>
      </nav>
    </header>

    <main class="layout">
      <section class="intro">
        <p class="intro__eyebrow">Document authenticity</p>
        <h1 class="intro__title">
          Verify <em>Letter</em>
        </h1>
        <p class="intro__text">
          Upload an official letter and get a clear authenticity check in moments — private, fast, and easy to follow.
        </p>

        <ul class="intro__tags">
          <li>
            <span class="intro__tag-icon" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none"><path d="M8 1.5l5.5 1.9v4.4c0 3.1-2.2 6-5.5 6.8-3.3-.8-5.5-3.7-5.5-6.8V3.4L8 1.5z" stroke="currentColor" stroke-width="1.2" /><path d="M6 8l1.5 1.5 3-3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" /></svg>
            </span>
            Encrypted upload
          </li>
          <li>
            <span class="intro__tag-icon" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="6" stroke="currentColor" stroke-width="1.2" /><path d="M8 5v3.5l2 1.2" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" /></svg>
            </span>
            Instant status
          </li>
          <li>
            <span class="intro__tag-icon" aria-hidden="true">
              <svg viewBox="0 0 16 16" fill="none"><path d="M8 2a4 4 0 100 8 4 4 0 000-8z" stroke="currentColor" stroke-width="1.2" /><path d="M4 13.5c.8-1.8 2.4-2.8 4-2.8s3.2 1 4 2.8" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" /></svg>
            </span>
            No account needed
          </li>
        </ul>

        <div class="visual" aria-hidden="true">
          <div class="visual__glow" />
          <div class="visual__card visual__card--back" />
          <div class="visual__card visual__card--front">
            <div class="visual__header">
              <div class="visual__seal">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M12 3.5l7.5 2.6v5.8c0 4.3-3 8.3-7.5 9.4-4.5-1.1-7.5-5.1-7.5-9.4V6.1L12 3.5z" stroke="currentColor" stroke-width="1.8" />
                  <path d="M9.2 12.1l2 2 3.8-3.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </div>
              <span class="visual__label">Official letter</span>
            </div>
            <div class="visual__lines">
              <span /><span /><span /><span />
            </div>
            <div class="visual__stamp">
              <svg viewBox="0 0 16 16" fill="none"><path d="M8 1.5l5.5 1.9v4.4c0 3.1-2.2 6-5.5 6.8-3.3-.8-5.5-3.7-5.5-6.8V3.4L8 1.5z" stroke="currentColor" stroke-width="1" /><path d="M6 8l1.5 1.5 3-3" stroke="currentColor" stroke-width="1" stroke-linecap="round" /></svg>
            </div>
          </div>
          <div class="visual__pill">
            <svg viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" /></svg>
            Valid
          </div>
        </div>
      </section>

      <section class="panel" :class="{ 'panel--success': verifyResult?.valid, 'panel--fail': verifyResult && !verifyResult.valid }">
        <template v-if="verifyResult?.valid">
          <article class="report" role="status">
            <div class="report__seal" aria-hidden="true">
              <svg viewBox="0 0 88 88" fill="none">
                <circle cx="44" cy="44" r="44" fill="url(#sealGrad)" />
                <path d="M44 22l16 5.6v12.4c0 9.2-6.4 17.8-16 20.2-9.6-2.4-16-11-16-20.2V27.6L44 22z" stroke="#fff" stroke-width="2.4" />
                <path d="M35.5 44.5l5.2 5.2 11.2-11.2" stroke="#fff" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round" />
                <defs>
                  <linearGradient id="sealGrad" x1="12" y1="8" x2="76" y2="80" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#0f569a" />
                    <stop offset="1" stop-color="#da4c77" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <p class="report__badge">Verification report</p>
            <h2 class="report__title">This letter is valid</h2>
            <p class="report__lead">
              Your document passed authenticity checks. The file matches the immutable blockchain record and has not been tampered with.
            </p>

            <div class="report__chips">
              <span v-for="chip in verifyResult.chips" :key="chip">{{ chip }}</span>
            </div>

            <div class="report__summary">
              <div v-for="item in verifyResult.highlights" :key="item.label" class="report__fact">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>

            <p class="report__verdict">{{ cleanVerdict(verifyResult.message) }}</p>

            <div class="report__actions">
              <a
                v-if="verifyResult.viewUrl"
                class="report__link"
                :href="verifyResult.viewUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                View on blockchain
              </a>
              <a
                v-if="verifyResult.verifyUrl"
                class="report__link report__link--ghost"
                :href="verifyResult.verifyUrl"
                target="_blank"
                rel="noopener noreferrer"
              >
                Open verification page
              </a>
            </div>

            <details v-if="verifyResult.details.length" class="report__more">
              <summary>Technical details</summary>
              <dl>
                <div v-for="item in verifyResult.details" :key="item.label" class="report__row">
                  <dt>{{ item.label }}</dt>
                  <dd>
                    <a
                      v-if="item.href"
                      :href="item.href"
                      target="_blank"
                      rel="noopener noreferrer"
                    >{{ item.value }}</a>
                    <template v-else>{{ item.value }}</template>
                  </dd>
                </div>
              </dl>
            </details>
          </article>

          <button class="cta" type="button" @click="resetVerification">
            Verify another letter
          </button>
        </template>

        <template v-else-if="verifyResult && !verifyResult.valid">
          <article class="report report--fail" role="status">
            <div class="report__seal report__seal--fail" aria-hidden="true">
              <svg viewBox="0 0 88 88" fill="none">
                <circle cx="44" cy="44" r="44" fill="url(#failGrad)" />
                <circle cx="44" cy="44" r="22" stroke="#fff" stroke-width="2.4" />
                <path d="M44 32v16M44 54.5v.5" stroke="#fff" stroke-width="3" stroke-linecap="round" />
                <defs>
                  <linearGradient id="failGrad" x1="12" y1="8" x2="76" y2="80" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#da4c77" />
                    <stop offset="1" stop-color="#0f569a" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <p class="report__badge report__badge--fail">Could not verify</p>
            <h2 class="report__title report__title--fail">This letter is not valid</h2>
            <p class="report__lead">
              We checked this file against Stichh’s signed records. It doesn’t match an authentic, untampered document.
            </p>

            <div class="report__chips report__chips--fail">
              <span v-for="chip in verifyResult.chips" :key="chip">{{ chip }}</span>
            </div>

            <div class="report__verdict report__verdict--fail">
              <strong>What we found</strong>
              <span>{{ cleanVerdict(verifyResult.message) }}</span>
            </div>

            <div v-if="verifyResult.highlights.length" class="report__summary">
              <div v-for="item in verifyResult.highlights" :key="item.label" class="report__fact">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>

            <ul class="report__tips">
              <li>Upload the original file, not a scanned or re-exported copy.</li>
              <li>Even a tiny edit changes the hash and fails verification.</li>
              <li>Ask the issuer to resend the signed letter from Stichh.</li>
            </ul>

            <details v-if="verifyResult.details.length" class="report__more report__more--fail">
              <summary>Technical details</summary>
              <dl>
                <div v-for="item in verifyResult.details" :key="item.label" class="report__row">
                  <dt>{{ item.label }}</dt>
                  <dd>{{ item.value }}</dd>
                </div>
              </dl>
            </details>
          </article>

          <div class="report__cta-row">
            <button class="cta" type="button" @click="triggerFilePicker">
              Try another file
            </button>
            <button class="cta cta--ghost" type="button" @click="resetVerification">
              Start over
            </button>
          </div>
          <input
            ref="fileInput"
            class="drop__input"
            type="file"
            accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
            @change="onFileChange"
          />
        </template>

        <template v-else>
          <div class="panel__head">
            <h2>{{ heading }}</h2>
            <p>{{ subtext }}</p>
          </div>

          <div
            class="drop"
            :class="{
              active: isDragging,
              filled: !!selectedFile,
              error: !!fileError
            }"
            @dragenter.prevent="onDragEnter"
            @dragover.prevent="onDragOver"
            @dragleave.prevent="onDragLeave"
            @drop.prevent="onDrop"
          >
            <input
              ref="fileInput"
              class="drop__input"
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
              @change="onFileChange"
            />

            <button
              v-if="!selectedFile"
              class="drop__empty"
              type="button"
              @click="triggerFilePicker"
            >
              <span class="drop__ring" aria-hidden="true">
                <span class="drop__icon">
                  <svg viewBox="0 0 56 56" fill="none">
                    <rect width="56" height="56" rx="16" fill="url(#upGrad)" />
                    <path d="M28 35V19M28 19l-7 7M28 19l7 7" stroke="#fff" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M18 33.5V37a5 5 0 005 5h10a5 5 0 005-5v-3.5" stroke="#fff" stroke-width="2.6" stroke-linecap="round" />
                    <defs>
                      <linearGradient id="upGrad" x1="8" y1="6" x2="48" y2="50" gradientUnits="userSpaceOnUse">
                        <stop stop-color="#0f569a" />
                        <stop offset="1" stop-color="#da4c77" />
                      </linearGradient>
                    </defs>
                  </svg>
                </span>
              </span>
              <strong>Drop your letter here</strong>
              <span class="drop__browse">or click to browse files</span>
              <span class="drop__formats">
                <span>PDF</span><span>DOC</span><span>DOCX</span><span>PNG</span><span>JPG</span>
              </span>
              <span class="drop__meta">Maximum file size · 10 MB</span>
            </button>

            <div v-else class="file">
              <div class="file__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none">
                  <path d="M7 3h8l4 4v14a1 1 0 01-1 1H7a1 1 0 01-1-1V4a1 1 0 011-1z" stroke="currentColor" stroke-width="1.7" />
                  <path d="M15 3v5h5" stroke="currentColor" stroke-width="1.7" />
                </svg>
              </div>
              <div class="file__info">
                <p class="file__name">{{ selectedFile.name }}</p>
                <p class="file__size">{{ formatBytes(selectedFile.size) }} · Ready</p>
              </div>
              <button class="file__replace" type="button" @click="triggerFilePicker">Replace</button>
              <button class="file__remove" type="button" aria-label="Remove file" @click="clearFile">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
                </svg>
              </button>
            </div>
          </div>

          <p v-if="fileError || apiError" class="msg msg--error" role="alert">{{ fileError || apiError }}</p>

          <button
            class="cta"
            type="button"
            :disabled="!selectedFile || isVerifying"
            @click="verifyFile"
          >
            <span v-if="isVerifying" class="cta__spin" aria-hidden="true" />
            <svg v-else viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 3.5l7.5 2.6v5.8c0 4.3-3 8.3-7.5 9.4-4.5-1.1-7.5-5.1-7.5-9.4V6.1L12 3.5z" stroke="currentColor" stroke-width="1.8" />
              <path d="M9.2 12.1l2 2 3.8-3.8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            {{ isVerifying ? 'Verifying…' : 'Verify letter' }}
          </button>

          <p class="panel__note">Your file stays private and is only used for this verification.</p>
        </template>
      </section>
    </main>
  </div>
</template>

<script>
const VERIFY_API_URL = 'https://sn3ezxtgcc.execute-api.us-east-1.amazonaws.com/dev/verifyDocument'

export default {
  name: 'VerifyLetters',
  data () {
    return {
      selectedFile: null,
      isDragging: false,
      dragDepth: 0,
      fileError: '',
      apiError: '',
      isVerifying: false,
      verifyResult: null
    }
  },
  computed: {
    step () {
      if (this.verifyResult) return 3
      if (this.isVerifying || this.selectedFile) return 2
      return 1
    },
    heading () {
      if (this.verifyResult) return this.verifyResult.valid ? 'Verification complete' : 'Check the result'
      if (this.selectedFile) return 'Letter ready'
      return 'Add your letter'
    },
    subtext () {
      if (this.verifyResult) return 'You can replace the file and verify again if needed.'
      if (this.selectedFile) return 'Looks good — hit verify when you’re ready.'
      return 'Drag & drop, or pick a file from your device.'
    }
  },
  methods: {
    triggerFilePicker () {
      this.$refs.fileInput?.click()
    },
    onDragEnter () {
      this.dragDepth += 1
      this.isDragging = true
    },
    onDragOver () {
      this.isDragging = true
    },
    onDragLeave () {
      this.dragDepth = Math.max(0, this.dragDepth - 1)
      if (this.dragDepth === 0) this.isDragging = false
    },
    onDrop (event) {
      this.dragDepth = 0
      this.isDragging = false
      const file = event.dataTransfer?.files?.[0]
      if (file) this.setFile(file)
    },
    onFileChange (event) {
      const file = event.target.files?.[0]
      if (file) this.setFile(file)
      event.target.value = ''
    },
    setFile (file) {
      const maxBytes = 10 * 1024 * 1024
      const allowed = [
        'application/pdf',
        'image/png',
        'image/jpeg',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ]
      const extensionOk = /\.(pdf|png|jpe?g|docx?)$/i.test(file.name)

      if (!allowed.includes(file.type) && !extensionOk) {
        this.fileError = 'Please upload a PDF, Word document, or image.'
        this.apiError = ''
        this.selectedFile = null
        this.verifyResult = null
        return
      }

      if (file.size > maxBytes) {
        this.fileError = 'That file is over 10 MB. Try a smaller one.'
        this.apiError = ''
        this.selectedFile = null
        this.verifyResult = null
        return
      }

      this.fileError = ''
      this.apiError = ''
      this.verifyResult = null
      this.selectedFile = file
    },
    clearFile () {
      this.selectedFile = null
      this.fileError = ''
      this.apiError = ''
      this.verifyResult = null
    },
    resetVerification () {
      this.clearFile()
    },
    cleanVerdict (text) {
      return String(text || '')
        .replace(/^(?:\u2705|\u274C|\u26A0\uFE0F|\u26A0)\s*/u, '')
        .trim()
    },
    formatBytes (bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },
    formatSignedAt (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    fileToBase64 (file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          const result = String(reader.result || '')
          resolve(result.includes(',') ? result.split(',')[1] : result)
        }
        reader.onerror = () => reject(new Error('Could not read the selected file.'))
        reader.readAsDataURL(file)
      })
    },
    unwrapPayload (payload) {
      if (!payload || typeof payload !== 'object') return payload

      if (typeof payload.body === 'string') {
        try {
          return JSON.parse(payload.body)
        } catch (_) {
          return { message: payload.body }
        }
      }

      if (payload.body && typeof payload.body === 'object') return payload.body
      return payload
    },
    parseVerifyResponse (payload) {
      const body = this.unwrapPayload(payload) || {}

      const status = String(body.Status || body.status || '').toUpperCase()
      const verified = body.verified === true || body.valid === true || body.is_valid === true
      const revoked = body.revoked === true
      const hasFileCheck = !!body.file_check
      const matchesOriginal = body.file_check?.matches_original === true

      const valid = verified &&
        !revoked &&
        status !== 'ERROR' &&
        (status === 'SUCCESS' || status === '' || status === 'OK') &&
        (hasFileCheck ? matchesOriginal : true) &&
        body.tamper_proof !== false

      const message = body.overall_verdict ||
        body.Message ||
        body.message ||
        body.file_check?.verdict ||
        (valid ? 'Document verified successfully.' : 'Document could not be verified.')

      const record = body.document_record || {}
      const tags = body.on_chain_tags || {}
      const chain = body.chain_info || {}

      const documentName = record.document_name || tags['Document-Name'] || this.selectedFile?.name
      const organization = tags['Organization-Name']
      const issuedBy = record.signed_by_name || tags['Issued-By']
      const issuedTo = tags['Issued-To']
      const signedAt = this.formatSignedAt(record.signed_at)
      const chainStatus = chain.chain_status || record.blockchain_status
      const txId = body.arweave_tx_id || chain.arweave_tx_id
      const viewUrl = chain.viewblock_url || chain.arweave_url || null
      const verifyUrl = record.verification_url || null
      const uploadedHash = body.file_check?.uploaded_file_hash || body.document_hash || ''

      const chips = []
      if (valid) {
        chips.push('Authentic')
        if (body.tamper_proof) chips.push('Tamper-proof')
        if (matchesOriginal) chips.push('Hash matched')
        if (!revoked) chips.push('Not revoked')
        if (chainStatus) chips.push(String(chainStatus))
      } else {
        chips.push('Not verified')
        if (status === 'ERROR') chips.push('Check failed')
        if (hasFileCheck && !matchesOriginal) chips.push('Hash mismatch')
        if (body.tamper_proof === false) chips.push('Not tamper-proof')
        if (body.content_checked) chips.push('Content checked')
        if (revoked) chips.push('Revoked')
      }

      const highlights = valid
        ? [
          { label: 'Document', value: documentName },
          { label: 'Organization', value: organization },
          { label: 'Issued by', value: issuedBy },
          { label: 'Issued to', value: issuedTo },
          { label: 'Signed on', value: signedAt }
        ].filter((item) => item.value)
        : [
          { label: 'File', value: documentName },
          { label: 'Status', value: status || 'ERROR' },
          { label: 'Verified', value: verified ? 'Yes' : 'No' },
          {
            label: 'Hash match',
            value: hasFileCheck ? (matchesOriginal ? 'Yes' : 'No') : ''
          },
          {
            label: 'Tamper-proof',
            value: body.tamper_proof === true ? 'Yes' : (body.tamper_proof === false ? 'No' : '')
          }
        ].filter((item) => item.value)

      const details = valid
        ? [
          { label: 'Document type', value: tags['Document-Type'] },
          { label: 'Purpose', value: tags['Purpose-of-Issue'] },
          { label: 'Chain status', value: chainStatus },
          { label: 'Confirmations', value: chain.confirmations != null ? String(chain.confirmations) : '' },
          {
            label: 'Arweave TX',
            value: txId,
            href: viewUrl
          },
          {
            label: 'Document hash',
            value: body.document_hash || tags['Document-Hash'] || record.document_hash_db
          }
        ].filter((item) => item.value)
        : [
          { label: 'Uploaded hash', value: uploadedHash },
          {
            label: 'Matches original',
            value: hasFileCheck ? (matchesOriginal ? 'true' : 'false') : ''
          },
          {
            label: 'Content checked',
            value: body.content_checked === true ? 'true' : (body.content_checked === false ? 'false' : '')
          }
        ].filter((item) => item.value)

      return {
        valid,
        message,
        chips,
        highlights,
        details,
        viewUrl,
        verifyUrl,
        raw: body
      }
    },
    async verifyFile () {
      if (!this.selectedFile || this.isVerifying) return

      this.isVerifying = true
      this.fileError = ''
      this.apiError = ''
      this.verifyResult = null

      try {
        const fileContent = await this.fileToBase64(this.selectedFile)
        const response = await fetch(VERIFY_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            command: 'verifyDocument',
            file_content: fileContent
          })
        })

        const rawText = await response.text()
        let payload = null
        try {
          payload = rawText ? JSON.parse(rawText) : null
        } catch (_) {
          payload = { message: rawText }
        }

        if (!response.ok) {
          const parsedError = this.parseVerifyResponse(payload)
          if (payload && (payload.Status || payload.status || payload.overall_verdict || payload.Message)) {
            this.verifyResult = parsedError
            return
          }
          const apiMessage = payload?.Message ||
            payload?.message ||
            payload?.error ||
            payload?.Status ||
            `Verification failed (${response.status}).`
          throw new Error(typeof apiMessage === 'string' ? apiMessage : 'Verification failed.')
        }

        this.verifyResult = this.parseVerifyResponse(payload)
      } catch (error) {
        const message = error?.message || 'Something went wrong while verifying. Please try again.'
        if (/failed to fetch|networkerror|network error|load failed/i.test(message)) {
          this.apiError = 'Could not reach the verification service. Please try again.'
        } else {
          this.apiError = message
        }
      } finally {
        this.isVerifying = false
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,600;12..96,700;12..96,800&family=Figtree:wght@400;500;600;700&display=swap');

.verify-page {
  --ink: #0b1f33;
  --muted: #5a6575;
  --brand: #0f569a;
  --brand-2: #da4c77;
  --line: rgba(15, 86, 154, 0.18);
  --glass: rgba(255, 255, 255, 0.72);
  --glass-border: rgba(255, 255, 255, 0.9);
  --shadow: 0 24px 64px rgba(11, 31, 51, 0.08);
  --shadow-lg: 0 32px 80px rgba(15, 86, 154, 0.12);
  --radius: 24px;
  --radius-lg: 32px;
  --display: 'Bricolage Grotesque', Georgia, sans-serif;
  --body: 'Figtree', system-ui, sans-serif;

  position: relative;
  isolation: isolate;
  min-height: 100vh;
  overflow-x: hidden;
  padding: clamp(1.25rem, 3vw, 2rem) clamp(1.25rem, 4vw, 2.5rem) 2.5rem;
  color: var(--ink);
  font-family: var(--body);
  background: #f4f7fb;
}

.verify-page *,
.verify-page *::before,
.verify-page *::after {
  box-sizing: border-box;
}

.verify-page button {
  font: inherit;
  appearance: none;
  -webkit-appearance: none;
}

.bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  overflow: hidden;
  background:
    radial-gradient(1000px 520px at 8% -8%, rgba(218, 76, 119, 0.16), transparent 58%),
    radial-gradient(800px 480px at 98% 102%, rgba(15, 86, 154, 0.14), transparent 55%),
    linear-gradient(165deg, #f5f8fc 0%, #eef2f8 42%, #faf5f8 100%);
}

.bg__blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(64px);
  animation: drift 18s ease-in-out infinite;
}

.bg__blob--a {
  top: -8rem;
  right: -5rem;
  width: 22rem;
  height: 22rem;
  background: rgba(218, 76, 119, 0.14);
}

.bg__blob--b {
  left: -6rem;
  bottom: -8rem;
  width: 20rem;
  height: 20rem;
  background: rgba(15, 86, 154, 0.1);
  animation-delay: -6s;
}

.topbar,
.layout {
  position: relative;
  z-index: 1;
  max-width: 1140px;
  margin-left: auto;
  margin-right: auto;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-bottom: clamp(1.75rem, 4vw, 2.75rem);
  animation: fadeUp 0.6s ease both;
}

.brand {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  flex-shrink: 0;
}

.brand__icon {
  display: grid;
  place-items: center;
  width: 2.35rem;
  height: 2.35rem;
  filter: drop-shadow(0 8px 16px rgba(15, 86, 154, 0.22));
}

.brand__icon svg {
  width: 100%;
  height: 100%;
}

.brand__name {
  font-family: var(--display);
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--ink);
}

.steps {
  display: flex;
  align-items: center;
  gap: 0;
  padding: 0.45rem 0.85rem;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--glass);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 4px 24px rgba(15, 86, 154, 0.06);
}

.steps__item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #9aa3b0;
  transition: color 0.3s ease;
}

.steps__item.active {
  color: var(--brand);
}

.steps__item.done {
  color: var(--brand);
}

.steps__n {
  display: grid;
  place-items: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  background: #e4ebf4;
  font-size: 0.68rem;
  font-weight: 700;
  transition: background 0.3s ease, transform 0.3s ease, box-shadow 0.3s ease;
}

.steps__n svg {
  width: 0.65rem;
  height: 0.65rem;
}

.steps__item.active .steps__n {
  background: linear-gradient(135deg, var(--brand) 0%, var(--brand-2) 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(15, 86, 154, 0.28);
  transform: scale(1.05);
}

.steps__item.done .steps__n {
  background: var(--brand);
  color: #fff;
}

.steps__t {
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.01em;
}

.steps__rule {
  width: 1.75rem;
  height: 2px;
  margin: 0 0.35rem;
  border-radius: 99px;
  background: #d5deea;
  transition: background 0.35s ease, transform 0.35s ease;
}

.steps__rule.on {
  background: linear-gradient(90deg, var(--brand), var(--brand-2));
}

.layout {
  display: grid;
  grid-template-columns: 1.08fr 0.92fr;
  gap: clamp(2rem, 5vw, 3.5rem);
  align-items: center;
  min-height: calc(100vh - 8rem);
}

.intro {
  animation: fadeUp 0.65s ease 0.08s both;
}

.intro__eyebrow {
  margin: 0 0 0.85rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--brand);
}

.intro__title {
  margin: 0;
  font-family: var(--display);
  font-size: clamp(2.85rem, 6.5vw, 4.6rem);
  font-weight: 800;
  line-height: 0.96;
  letter-spacing: -0.045em;
}

.intro__title em {
  font-style: normal;
  background: linear-gradient(120deg, var(--brand) 0%, var(--brand-2) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.intro__text {
  margin: 1.15rem 0 0;
  max-width: 30rem;
  font-size: clamp(1rem, 1.4vw, 1.08rem);
  line-height: 1.6;
  color: var(--muted);
}

.intro__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin: 1.5rem 0 0;
  padding: 0;
  list-style: none;
}

.intro__tags li {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.85rem;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: var(--glass);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--ink);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
}

.intro__tags li:hover {
  transform: translateY(-1px);
  border-color: rgba(15, 86, 154, 0.28);
  box-shadow: 0 8px 20px rgba(15, 86, 154, 0.08);
}

.intro__tag-icon {
  display: grid;
  place-items: center;
  width: 1.25rem;
  height: 1.25rem;
  color: var(--brand);
}

.intro__tag-icon svg {
  width: 100%;
  height: 100%;
}

.visual {
  position: relative;
  width: min(100%, 280px);
  height: 210px;
  margin-top: 2.25rem;
}

.visual__glow {
  position: absolute;
  inset: 10% 5% 15%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(15, 86, 154, 0.12), transparent 70%);
  filter: blur(20px);
}

.visual__card {
  position: absolute;
  border-radius: 18px;
  background: #fff;
}

.visual__card--back {
  inset: 20px 28px 6px 6px;
  background: linear-gradient(145deg, #dce6f2, #e8eef6);
  transform: rotate(-8deg);
  box-shadow: 0 16px 40px rgba(15, 86, 154, 0.1);
}

.visual__card--front {
  inset: 4px 10px 18px 26px;
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 1rem 1.05rem;
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: var(--shadow-lg);
  transform: rotate(4deg);
  animation: float 5s ease-in-out infinite;
}

.visual__header {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.visual__seal {
  width: 2rem;
  height: 2rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: #fff;
  background: linear-gradient(145deg, var(--brand) 0%, var(--brand-2) 100%);
  box-shadow: 0 6px 14px rgba(15, 86, 154, 0.25);
}

.visual__seal svg {
  width: 1rem;
  height: 1rem;
}

.visual__label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--muted);
}

.visual__lines {
  display: grid;
  gap: 0.45rem;
}

.visual__lines span {
  display: block;
  height: 6px;
  border-radius: 99px;
  background: linear-gradient(90deg, #e8eef6, #f0f4f9);
}

.visual__lines span:nth-child(1) { width: 55%; }
.visual__lines span:nth-child(2) { width: 88%; }
.visual__lines span:nth-child(3) { width: 72%; }
.visual__lines span:nth-child(4) { width: 48%; }

.visual__stamp {
  align-self: flex-end;
  width: 1.65rem;
  height: 1.65rem;
  display: grid;
  place-items: center;
  border-radius: 50%;
  color: var(--brand);
  background: rgba(15, 86, 154, 0.08);
  border: 1px dashed rgba(15, 86, 154, 0.2);
}

.visual__stamp svg {
  width: 0.85rem;
  height: 0.85rem;
}

.visual__pill {
  position: absolute;
  right: -4px;
  bottom: 1.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.42rem 0.85rem;
  border-radius: 999px;
  background: var(--ink);
  color: #ffd6e4;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  box-shadow: 0 12px 28px rgba(11, 31, 51, 0.25);
  animation: bob 3.8s ease-in-out 0.4s infinite;
}

.visual__pill svg {
  width: 0.7rem;
  height: 0.7rem;
  color: #ffd6e4;
}

.panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: clamp(1.35rem, 2.5vw, 1.75rem);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-lg);
  background: var(--glass);
  box-shadow: var(--shadow);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  animation: fadeUp 0.7s ease 0.14s both;
  transition: box-shadow 0.35s ease;
}

.panel:hover {
  box-shadow: var(--shadow-lg);
}

.panel__head h2 {
  margin: 0;
  font-family: var(--display);
  font-size: clamp(1.4rem, 2.2vw, 1.65rem);
  font-weight: 700;
  letter-spacing: -0.025em;
}

.panel__head p {
  margin: 0.35rem 0 0;
  color: var(--muted);
  font-size: 0.94rem;
  line-height: 1.5;
}

.drop {
  position: relative;
  border: 1.5px dashed rgba(15, 86, 154, 0.24);
  border-radius: var(--radius);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.6), rgba(238, 242, 248, 0.5));
  transition:
    border-color 0.3s ease,
    background 0.3s ease,
    box-shadow 0.3s ease,
    transform 0.3s ease;
}

.drop::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  opacity: 0;
  background: linear-gradient(135deg, rgba(15, 86, 154, 0.04), rgba(218, 76, 119, 0.06));
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.drop:hover::before {
  opacity: 1;
}

.drop.active {
  border-style: solid;
  border-color: var(--brand);
  background: rgba(234, 241, 250, 0.85);
  box-shadow:
    0 0 0 4px rgba(15, 86, 154, 0.1),
    0 0 0 8px rgba(218, 76, 119, 0.06);
  transform: scale(1.005);
}

.drop.filled {
  border-style: solid;
  border-color: rgba(15, 86, 154, 0.22);
  background: rgba(245, 248, 252, 0.9);
  padding: 0.9rem;
}

.drop.error {
  border-color: #c45c5c;
  background: #fff8f8;
}

.drop__input {
  display: none;
}

.drop__empty {
  width: 100%;
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 1.75rem 1.25rem;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: transform 0.25s ease;
}

.drop__empty:hover {
  transform: translateY(-2px);
}

.drop__empty:focus-visible {
  outline: none;
}

.drop__empty:focus-visible .drop__ring {
  box-shadow:
    0 0 0 4px rgba(15, 86, 154, 0.15),
    0 12px 32px rgba(15, 86, 154, 0.18);
}

.drop__ring {
  display: grid;
  place-items: center;
  width: 4.5rem;
  height: 4.5rem;
  margin-bottom: 0.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 8px 24px rgba(15, 86, 154, 0.1);
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.drop__empty:hover .drop__ring {
  transform: scale(1.06);
  box-shadow: 0 12px 32px rgba(15, 86, 154, 0.16);
}

.drop__icon {
  width: 3.25rem;
  height: 3.25rem;
  animation: bob 3.5s ease-in-out infinite;
}

.drop__icon svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 8px 14px rgba(15, 86, 154, 0.22));
}

.drop__empty strong {
  font-size: 1.06rem;
  font-weight: 700;
  color: var(--ink);
}

.drop__browse {
  color: var(--brand);
  font-weight: 600;
  font-size: 0.92rem;
  text-decoration: underline;
  text-underline-offset: 3px;
  transition: color 0.2s ease;
}

.drop__empty:hover .drop__browse {
  color: var(--brand-2);
}

.drop__formats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.35rem;
  margin-top: 0.65rem;
}

.drop__formats span {
  padding: 0.22rem 0.5rem;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(15, 86, 154, 0.12);
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: var(--brand);
}

.drop__meta {
  margin-top: 0.5rem;
  font-size: 0.78rem;
  color: var(--muted);
}

.file {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
  padding: 0.95rem 1rem;
  border: 1px solid rgba(15, 86, 154, 0.1);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 8px 24px rgba(15, 86, 154, 0.06);
  animation: fadeUp 0.35s ease both;
}

.file__icon {
  width: 2.65rem;
  height: 2.65rem;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border-radius: 12px;
  color: var(--brand);
  background: linear-gradient(145deg, rgba(15, 86, 154, 0.08), rgba(218, 76, 119, 0.06));
}

.file__icon svg {
  width: 1.15rem;
  height: 1.15rem;
}

.file__info {
  min-width: 0;
  flex: 1;
}

.file__name {
  margin: 0;
  font-size: 0.92rem;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.file__size {
  margin: 0.2rem 0 0;
  font-size: 0.76rem;
  color: var(--muted);
}

.file__replace {
  flex-shrink: 0;
  height: 2.1rem;
  padding: 0 0.85rem;
  border: 1px solid var(--line);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.9);
  color: var(--brand);
  font-size: 0.76rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
}

.file__replace:hover {
  background: #fff;
  border-color: rgba(15, 86, 154, 0.3);
  transform: translateY(-1px);
}

.file__remove {
  width: 2.1rem;
  height: 2.1rem;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border: 0;
  border-radius: 999px;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
}

.file__remove:hover {
  background: #fff1f1;
  color: #a13b3b;
}

.file__remove svg {
  width: 0.9rem;
  height: 0.9rem;
}

.msg {
  display: grid;
  gap: 0.2rem;
  padding: 0.85rem 0.95rem;
  border-radius: 14px;
  font-size: 0.88rem;
  line-height: 1.4;
}

.msg strong {
  font-size: 0.92rem;
}

.msg--error {
  color: #a13b3b;
  background: #fff1f1;
}

.panel--success {
  gap: 1rem;
  padding: 1.5rem;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(255, 255, 255, 0.88)),
    linear-gradient(145deg, rgba(15, 86, 154, 0.06), rgba(218, 76, 119, 0.08));
}

.panel--fail {
  gap: 1rem;
  padding: 1.5rem;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.97), rgba(255, 248, 250, 0.92)),
    linear-gradient(145deg, rgba(218, 76, 119, 0.08), rgba(15, 86, 154, 0.05));
}

.report {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.65rem;
  animation: rise 0.45s ease both;
}

.report__seal {
  width: 4.75rem;
  height: 4.75rem;
  margin-bottom: 0.25rem;
  filter: drop-shadow(0 14px 24px rgba(15, 86, 154, 0.28));
  animation: bob 3.2s ease-in-out infinite;
}

.report__seal svg {
  width: 100%;
  height: 100%;
}

.report__badge {
  margin: 0;
  padding: 0.28rem 0.7rem;
  border-radius: 999px;
  background: rgba(15, 86, 154, 0.1);
  color: var(--brand);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.report__title {
  margin: 0;
  font-family: var(--display);
  font-size: clamp(1.7rem, 3vw, 2.15rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  line-height: 1.1;
  background: linear-gradient(120deg, var(--brand) 0%, var(--brand-2) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.report__lead {
  margin: 0;
  max-width: 28rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--muted);
}

.report__chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.4rem;
  margin-top: 0.25rem;
}

.report__chips span {
  padding: 0.35rem 0.7rem;
  border-radius: 999px;
  border: 1px solid rgba(15, 86, 154, 0.16);
  background: rgba(255, 255, 255, 0.9);
  color: var(--ink);
  font-size: 0.76rem;
  font-weight: 700;
}

.report__summary {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.55rem;
  width: 100%;
  margin-top: 0.55rem;
}

.report__fact {
  display: grid;
  gap: 0.2rem;
  padding: 0.75rem 0.85rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(15, 86, 154, 0.1);
  text-align: left;
}

.report__fact span {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--muted);
}

.report__fact strong {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.report__verdict {
  margin: 0.35rem 0 0;
  width: 100%;
  padding: 0.85rem 1rem;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(15, 86, 154, 0.08), rgba(218, 76, 119, 0.1));
  border: 1px solid rgba(15, 86, 154, 0.12);
  font-size: 0.88rem;
  line-height: 1.45;
  color: var(--ink);
  text-align: left;
}

.report__verdict--fail {
  display: grid;
  gap: 0.35rem;
  background: linear-gradient(135deg, rgba(218, 76, 119, 0.1), rgba(15, 86, 154, 0.06));
  border-color: rgba(218, 76, 119, 0.22);
}

.report__verdict--fail strong {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--brand-2);
}

.report__verdict--fail span {
  color: var(--ink);
  font-weight: 500;
}

.report__badge--fail {
  background: rgba(218, 76, 119, 0.12);
  color: var(--brand-2);
}

.report__title--fail {
  background: linear-gradient(120deg, var(--brand-2) 0%, var(--brand) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.report__seal--fail {
  filter: drop-shadow(0 14px 24px rgba(218, 76, 119, 0.28));
}

.report__chips--fail span {
  border-color: rgba(218, 76, 119, 0.2);
  color: var(--ink);
  background: rgba(255, 255, 255, 0.95);
}

.report__tips {
  width: 100%;
  margin: 0.15rem 0 0;
  padding: 0.9rem 1rem 0.9rem 1.85rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(15, 86, 154, 0.1);
  text-align: left;
  color: var(--muted);
  font-size: 0.84rem;
  line-height: 1.5;
}

.report__tips li + li {
  margin-top: 0.35rem;
}

.report__more--fail summary {
  color: var(--brand-2);
}

.report__cta-row {
  display: grid;
  gap: 0.65rem;
  width: 100%;
}

.cta--ghost {
  background: #fff;
  color: var(--brand);
  border: 1px solid rgba(15, 86, 154, 0.22);
  box-shadow: none;
}

.cta--ghost:hover:not(:disabled) {
  background: #f7f9fc;
  box-shadow: 0 8px 20px rgba(15, 86, 154, 0.1);
  transform: translateY(-1px);
}

.report__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.55rem;
  width: 100%;
  margin-top: 0.25rem;
}

.report__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 2.4rem;
  padding: 0 0.95rem;
  border-radius: 999px;
  background: linear-gradient(135deg, var(--brand) 0%, var(--brand-2) 100%);
  color: #fff;
  font-size: 0.86rem;
  font-weight: 700;
  text-decoration: none;
}

.report__link--ghost {
  background: #fff;
  color: var(--brand);
  border: 1px solid rgba(15, 86, 154, 0.2);
}

.report__more {
  width: 100%;
  margin-top: 0.35rem;
  border-radius: 14px;
  border: 1px solid rgba(15, 86, 154, 0.12);
  background: rgba(255, 255, 255, 0.75);
  text-align: left;
  overflow: hidden;
}

.report__more summary {
  cursor: pointer;
  list-style: none;
  padding: 0.8rem 0.95rem;
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--brand);
}

.report__more summary::-webkit-details-marker {
  display: none;
}

.report__more[open] summary {
  border-bottom: 1px solid rgba(15, 86, 154, 0.1);
}

.report__more dl {
  margin: 0;
  padding: 0.75rem 0.95rem 0.95rem;
  display: grid;
  gap: 0.55rem;
}

.report__row {
  display: grid;
  grid-template-columns: 7.2rem 1fr;
  gap: 0.45rem;
  font-size: 0.8rem;
}

.report__row dt {
  margin: 0;
  color: var(--muted);
  font-weight: 600;
}

.report__row dd {
  margin: 0;
  color: var(--ink);
  font-weight: 600;
  overflow-wrap: anywhere;
}

.report__row a {
  color: var(--brand);
  text-decoration: underline;
  text-underline-offset: 2px;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 560px) {
  .report__summary {
    grid-template-columns: 1fr;
  }

  .report__row {
    grid-template-columns: 1fr;
    gap: 0.15rem;
  }
}

.cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  width: 100%;
  min-height: 3.25rem;
  border: 0;
  border-radius: 16px;
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  background: linear-gradient(135deg, var(--brand) 0%, var(--brand-2) 100%);
  box-shadow: 0 14px 32px rgba(15, 86, 154, 0.28);
  transition: transform 0.25s ease, box-shadow 0.25s ease, opacity 0.25s ease;
}

.cta svg {
  width: 1.1rem;
  height: 1.1rem;
}

.cta:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 20px 40px rgba(218, 76, 119, 0.3);
}

.cta:active:not(:disabled) {
  transform: translateY(0);
}

.cta:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
}

.cta__spin {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.verify-page.is-ready .cta:not(:disabled) {
  animation: pulseCta 2.5s ease-in-out infinite;
}

.panel__note {
  margin: 0;
  text-align: center;
  font-size: 0.78rem;
  line-height: 1.45;
  color: var(--muted);
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0) rotate(4deg); }
  50% { transform: translateY(-8px) rotate(4deg); }
}

@keyframes bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

@keyframes drift {
  0%, 100% { transform: translate(0, 0); }
  50% { transform: translate(12px, -10px); }
}

@keyframes pulseCta {
  0%, 100% { box-shadow: 0 14px 32px rgba(15, 86, 154, 0.28); }
  50% { box-shadow: 0 18px 40px rgba(218, 76, 119, 0.35); }
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 900px) {
  .topbar {
    flex-direction: column;
    align-items: stretch;
    gap: 1rem;
  }

  .brand {
    justify-content: center;
  }

  .steps {
    justify-content: center;
    align-self: center;
  }

  .layout {
    grid-template-columns: 1fr;
    min-height: auto;
    gap: 2rem;
  }

  .intro {
    text-align: center;
  }

  .intro__text {
    margin-left: auto;
    margin-right: auto;
  }

  .intro__tags {
    justify-content: center;
  }

  .visual {
    margin-left: auto;
    margin-right: auto;
  }

  .panel {
    max-width: 520px;
    margin-left: auto;
    margin-right: auto;
    width: 100%;
  }
}

@media (max-width: 560px) {
  .steps__t {
    display: none;
  }

  .steps__rule {
    width: 1.25rem;
    margin: 0 0.2rem;
  }

  .file {
    flex-wrap: wrap;
  }

  .file__replace,
  .file__remove {
    margin-left: auto;
  }

  .drop__formats {
    gap: 0.25rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .topbar,
  .intro,
  .panel,
  .file,
  .visual__card--front,
  .visual__pill,
  .drop__icon,
  .bg__blob,
  .cta__spin,
  .verify-page.is-ready .cta:not(:disabled),
  .report {
    animation: none !important;
  }

  .visual__card--front {
    transform: rotate(4deg);
  }

  .drop,
  .cta,
  .intro__tags li,
  .file__replace {
    transition: none;
  }
}
</style>
